import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, UserPlus, Trash2, Loader2, Crown } from "lucide-react";
import client from "@/api/client";
import { toast } from "sonner";

/**
 * CompanyTeamCard — the owner invites co-founders/employees. Invited members
 * share the company (same success indicators) and get data-room access.
 */
export function CompanyTeamCard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("editor");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await client.get("/my-company/team");
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy("invite");
    try {
      await client.post("/my-company/team", { email: email.trim(), role });
      toast.success(t("companyTeam.added", { defaultValue: "Team member added" }));
      setEmail("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy("");
    }
  };

  const changeRole = async (m, nextRole) => {
    setBusy(m.membership_id);
    try {
      await client.patch(`/my-company/team/${m.membership_id}`, { role: nextRole });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy("");
    }
  };

  const remove = async (m) => {
    setBusy(m.membership_id);
    try {
      await client.delete(`/my-company/team/${m.membership_id}`);
      toast.success(t("companyTeam.removed", { defaultValue: "Team member removed" }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy("");
    }
  };

  if (!data?.linked) return null;

  return (
    <Card className="rounded-2xl border border-border bg-card p-4 sm:p-6" data-testid="company-team-card">
      <div className="flex items-start gap-3 pb-4 mb-4 border-b border-border">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Users className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h3 className="font-display text-base md:text-lg font-semibold leading-tight">
            {t("companyTeam.title", { defaultValue: "Your team" })}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {t("companyTeam.hint", { defaultValue: "Invited teammates share your success indicators and data room." })}
          </p>
        </div>
      </div>

      <ul className="space-y-1.5" data-testid="company-team-list">
        <li className="flex items-center gap-2.5 rounded-xl border border-primary/25 bg-primary/5 px-3 py-2">
          <Crown className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium truncate">{data.owner?.name || data.owner?.email}</span>
            <span className="block text-[11px] text-muted-foreground">
              {t("companyTeam.owner", { defaultValue: "Owner" })}
            </span>
          </span>
        </li>
        {(data.members || []).map((m) => (
          <li key={m.membership_id} className="flex flex-wrap items-center gap-2 rounded-xl border border-border px-3 py-2" data-testid={`company-team-member-${m.membership_id}`}>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-medium truncate">{m.name || m.email}</span>
              <span className="block text-[11px] text-muted-foreground truncate">{m.email}</span>
            </span>
            {m.is_me ? (
              <Badge variant="outline" className="rounded-full text-[10px]">{m.role}</Badge>
            ) : (
              <>
                <Select value={m.role} onValueChange={(v) => changeRole(m, v)} disabled={busy === m.membership_id}>
                  <SelectTrigger className="h-8 w-[120px] rounded-lg text-xs" data-testid={`company-team-role-${m.membership_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="editor">{t("companyTeam.roleEditor", { defaultValue: "Can edit" })}</SelectItem>
                    <SelectItem value="viewer">{t("companyTeam.roleViewer", { defaultValue: "Can view" })}</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8 rounded-lg text-muted-foreground hover:text-rose-600"
                  onClick={() => remove(m)}
                  disabled={busy === m.membership_id}
                  aria-label={t("common.delete", { defaultValue: "Remove" })}
                  data-testid={`company-team-remove-${m.membership_id}`}
                >
                  {busy === m.membership_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Input
          type="email"
          className="rounded-xl h-9 flex-1"
          dir="ltr"
          placeholder={t("companyTeam.emailPlaceholder", { defaultValue: "teammate@company.com" })}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); invite(); } }}
          data-testid="company-team-email"
        />
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="h-9 rounded-xl sm:w-[130px]" data-testid="company-team-new-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="editor">{t("companyTeam.roleEditor", { defaultValue: "Can edit" })}</SelectItem>
            <SelectItem value="viewer">{t("companyTeam.roleViewer", { defaultValue: "Can view" })}</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" size="sm" className="rounded-xl h-9 gap-2" disabled={busy === "invite"} onClick={invite} data-testid="company-team-invite">
          {busy === "invite" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
          {t("companyTeam.invite", { defaultValue: "Add" })}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">
        {t("companyTeam.signupNote", { defaultValue: "They need an account first — ask them to sign up, then add them here." })}
      </p>
    </Card>
  );
}

export default CompanyTeamCard;
